/* ═══════════════════════════════════════════════════════
   NYC Suite — deterministic quote PDF composer.

   html2pdf's automatic pagination computes page-break spacers from fractional
   element positions, and Safari and Chrome round those differently — the same
   quote exported clean in Chrome and full of blank/drifted pages in Safari
   (Freefall, 2026-07-02, three failed fixes). So we do not use its pagination
   at all: each [data-quote-page] div is rasterized with html2canvas and placed
   onto letter-size jsPDF pages ourselves. One div = one page, by construction,
   in every browser.

   The only variable-height div is the main quote page. When it is taller than
   one letter page, we slice its canvas at page boundaries, nudging each cut UP
   to the nearest gap so it never lands inside a table row or a block marked
   data-keep-together (the same guarantee html2pdf's "avoid" gave, but ours).
   ═══════════════════════════════════════════════════════ */
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const PAGE_W_PX = 816;   // 8.5in @ 96dpi — matches S.page width in QuotePrintable
const PAGE_H_PX = 1056;  // 11in  @ 96dpi
const SCALE = 2;         // raster scale (crispness)
const CONT_TOP_PX = 40;  // whitespace above content on continuation slices

// Find a cut line at or above `wantY` (px from div top) that doesn't pass through
// any row or keep-together block. Walks upward; gives up (hard cut) rather than
// cutting above the previous cut.
function safeCutY(el, elTop, wantY, minY) {
  const blocks = [...el.querySelectorAll("tr, [data-keep-together]")]
    .map((b) => {
      const r = b.getBoundingClientRect();
      return { top: r.top - elTop, bottom: r.bottom - elTop };
    })
    .filter((b) => b.bottom - b.top < PAGE_H_PX); // a block taller than a page must be cut regardless
  let y = wantY;
  for (let guard = 0; guard < 50; guard += 1) {
    const hit = blocks.find((b) => b.top < y - 1 && b.bottom > y + 1);
    if (!hit) return y;
    y = hit.top - 6;
    if (y <= minY + 80) return wantY; // no safe gap in range — hard cut beats infinite regress
  }
  return y;
}

/** Compose the printable (a container of [data-quote-page] divs) into a jsPDF doc. */
export async function composeQuotePdf(container) {
  const pages = [...container.querySelectorAll("[data-quote-page]")];
  const pdf = new jsPDF({ unit: "in", format: "letter", orientation: "portrait" });
  let first = true;

  for (const el of pages) {
    const canvas = await html2canvas(el, {
      scale: SCALE,
      useCORS: true,
      backgroundColor: "#ffffff",
      windowWidth: Math.max(document.documentElement.clientWidth, PAGE_W_PX),
    });
    const cssH = canvas.height / SCALE;

    if (cssH <= PAGE_H_PX + 2) {
      // fits one page (all rate-sheet / reference pages — fixed 1056px)
      if (!first) pdf.addPage("letter", "portrait");
      first = false;
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, 8.5, cssH / 96);
    } else {
      // variable-height page (the main quote page): slice at safe cut lines
      const elTop = el.getBoundingClientRect().top;
      let y = 0;
      while (y < cssH - 2) {
        const room = y === 0 ? PAGE_H_PX : PAGE_H_PX - CONT_TOP_PX;
        const want = Math.min(y + room, cssH);
        const cut = want >= cssH ? cssH : safeCutY(el, elTop, want, y);
        const sliceH = cut - y;
        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = Math.max(1, Math.round(sliceH * SCALE));
        slice.getContext("2d").drawImage(
          canvas,
          0, Math.round(y * SCALE), canvas.width, slice.height,
          0, 0, slice.width, slice.height,
        );
        if (!first) pdf.addPage("letter", "portrait");
        first = false;
        pdf.addImage(
          slice.toDataURL("image/jpeg", 0.95), "JPEG",
          0, y === 0 ? 0 : CONT_TOP_PX / 96, 8.5, sliceH / 96,
        );
        y = cut;
      }
    }
  }
  return pdf;
}

export async function exportQuotePdf(container, filename) {
  const pdf = await composeQuotePdf(container);
  pdf.save(`${filename}.pdf`);
}

/** For harness/testing: return the PDF as a base64 string instead of downloading. */
export async function quotePdfBase64(container) {
  const pdf = await composeQuotePdf(container);
  return pdf.output("datauristring").split(",")[1];
}
